import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, FileText, RefreshCw, AlertCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { Blog, BlogStatus, GetBlogsParams } from '../../services/admin/adminService';
import DataTable, { Column } from '../../components/common/DataTable';
import { getImageUrl } from '../../utils/imageHelper';

const BlogManagement: React.FC = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statistics, setStatistics] = useState({
    draft: 0,
    published: 0,
    featured: 0,
    total: 0
  });
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; blog: Blog | null }>({
    show: false,
    blog: null
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const params: GetBlogsParams = {
        page: 1,
        limit: 100, // Fetch enough data for client-side pagination
      };

      const data = await adminService.getAllBlogs(params);
      setBlogs(data.blogs);
      setStatistics(data.statistics);
      
      if (showToast) {
        toast.success('Blogs refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch blogs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteBlog = async () => {
    if (!deleteModal.blog) return;
    
    try {
      setDeleting(true);
      await adminService.deleteBlog(deleteModal.blog.id);
      toast.success('Blog deleted successfully!');
      setDeleteModal({ show: false, blog: null });
      fetchBlogs();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete blog');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewBlog = (blog: Blog) => {
    navigate(`/admin/blogs/${blog.id}`);
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Blog Management</h1>
          <p className="text-secondary-600 mt-1">Manage blog posts and articles</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => fetchBlogs(true)}
            disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => navigate('/admin/blogs/create')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Create Blog
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Total Blogs</div>
          <div className="text-2xl font-bold text-secondary-900 mt-1">{statistics.total}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Draft</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">{statistics.draft}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Published</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{statistics.published}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Featured</div>
          <div className="text-2xl font-bold text-primary-600 mt-1">{statistics.featured}</div>
        </div>
      </div>

      {/* Blogs Table */}
      <DataTable
        data={blogs}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search blogs by title, description, or content..."
        searchFields={['title', 'description', 'content']}
        filterable={true}
        filterOptions={[
          { label: 'All', value: 'all', field: 'status' },
          { label: 'Draft', value: 'draft', field: 'status' },
          { label: 'Published', value: 'published', field: 'status' },
          { label: 'Archived', value: 'archived', field: 'status' },
          { label: 'Featured', value: 'featured', field: 'featured' },
        ]}
        defaultPageSize={10}
        emptyMessage="No blogs found"
        emptyIcon={<FileText size={64} className="text-secondary-300 mx-auto mb-4" />}
        columns={[
          {
            header: 'Blog',
            accessor: (blog) => (
              <div className="flex items-center gap-3">
                {blog.image ? (
                  <img
                    src={getImageUrl(blog.image) || blog.image}
                    alt={blog.title}
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-primary-100 flex items-center justify-center">
                    <FileText size={24} className="text-primary-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-secondary-900 truncate">
                    {blog.title}
                  </h3>
                  <p className="text-xs text-secondary-500 mt-1 line-clamp-2">
                    {blog.description}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-secondary-400">{blog.category}</span>
                    <span className="text-xs text-secondary-400">•</span>
                    <span className="text-xs text-secondary-400">{blog.author}</span>
                  </div>
                </div>
              </div>
            ),
            sortable: true,
          },
          {
            header: 'Status',
            accessor: (blog) => (
              <span
                className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${
                  blog.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : blog.status === 'draft'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {blog.status.charAt(0).toUpperCase() + blog.status.slice(1)}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Featured',
            accessor: (blog) => {
              const isFeatured = Boolean(blog.featured);
              return (
                <span
                  className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${
                    isFeatured
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {isFeatured ? 'Yes' : 'No'}
                </span>
              );
            },
            align: 'center',
            sortable: true,
          },
          {
            header: 'Read Time',
            accessor: (blog) => (
              <span className="text-sm text-secondary-600">
                {blog.read_time} min
              </span>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Date',
            accessor: (blog) => (
              <span className="text-sm text-secondary-600">
                {new Date(blog.created_at).toLocaleDateString()}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
        ]}
        renderActions={(blog) => (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewBlog(blog);
              }}
              className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-colors"
              title="View Blog"
            >
              <Eye size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/blogs/${blog.id}/edit`);
              }}
              className="bg-primary-50 hover:bg-primary-100 text-primary-600 p-2 rounded-lg transition-colors"
              title="Edit Blog"
            >
              <Edit2 size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteModal({ show: true, blog });
              }}
              className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors"
              title="Delete Blog"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
        onRowClick={handleViewBlog}
      />

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.blog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900">Delete Blog</h3>
              </div>
              
              <p className="text-secondary-600 mb-6">
                Are you sure you want to delete the blog{' '}
                <strong>{deleteModal.blog.title}</strong>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteBlog}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteModal({ show: false, blog: null })}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
